import React from 'react'
import { Power, RefreshCw, ShieldAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ErrorBoundaryProps {
  fallback?: React.ReactNode
  onReset?: () => void
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
    this.props.onReset?.()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-background p-8 text-foreground">
          <div className="relative flex flex-col items-center gap-6 text-center">
            <div className="rounded-full bg-destructive/10 p-4 ring-1 ring-destructive/20">
              <ShieldAlert className="h-12 w-12 text-destructive animate-pulse" />
            </div>
            
            <div className="flex flex-col gap-2">
              <h1 className="text-4xl font-extrabold tracking-tighter text-primary sm:text-5xl lg:text-6xl uppercase glitch-text">
                SIGNAL LOST
              </h1>
              <p className="max-w-md mx-auto text-sm text-muted-foreground font-medium leading-relaxed">
                Critical system breach or transmission error detected. <br />
                The hunt has been interrupted.
              </p>
            </div>

            {this.state.error && (
              <div className="mt-4 rounded-md border border-foreground/5 bg-foreground/5 p-3 font-mono text-xs text-muted-foreground/85">
                ERRORCODE: {this.state.error.name || 'UNKNOWN_BREACH'}
                <br />
                PAYLOAD: {this.state.error.message.substring(0, 50)}...
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-md bg-primary px-8 text-sm font-bold text-primary-foreground transition-[transform,background-color] hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] ring-offset-background focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Power className="h-4 w-4" />
                REBOOT PROTOCOL
              </button>
              
              <button
                type="button"
                onClick={this.handleReset}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-md border border-foreground/10 bg-foreground/5 px-8 text-sm font-bold text-foreground transition-[transform,background-color] hover:bg-foreground/10 hover:scale-[1.02] active:scale-[0.98]"
              >
                <RefreshCw className="h-4 w-4" />
                RESET TRANSMISSION
              </button>
            </div>
          </div>
          
          <style>{`
            .glitch-text {
              text-shadow: 2px 0 var(--destructive), -2px 0 var(--primary);
              animation: glitch 2s infinite alternate-reverse;
            }
            @keyframes glitch {
              0% { transform: translate(0); }
              20% { transform: translate(-2px, 2px); }
              40% { transform: translate(-2px, -2px); }
              60% { transform: translate(2px, 2px); }
              80% { transform: translate(2px, -2px); }
              100% { transform: translate(0); }
            }
          `}</style>
        </div>
      )
    }

    return this.props.children
  }
}

export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  )
}
